import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { getHeroDimensions } from 'Components/widgets/hero/heroUtils';
import { ImmersiveSlideShowControls } from 'Components/widgets/immersive-slideshow/controls/ImmersiveSlideShowControls';
type Props = {
  className?: string;
  controlsTheme?: string;
  isPaginationDisabled?: boolean;
  immersiveSize?: string;
  items: Array<React.ReactNode>;
  nextLabel?: string;
  previousLabel?: string;
};

const getInitialDisplayedIndex = (length: number) =>
  Array(length)
    .fill(false)
    .map((_, idx) => idx === length - 1 || idx < 2);

export const ImmersiveSlideShowLarge = ({
  className,
  controlsTheme = 'default',
  isPaginationDisabled = true,
  immersiveSize = 'large',
  items = [],
  nextLabel,
  previousLabel,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(getInitialDisplayedIndex(items.length));
  useEffect(() => {
    if (displayedIndex.every(Boolean)) {
      return;
    }

    const newDisplayedIndex = displayedIndex.map(
      (isDisplayed, idx) => isDisplayed || idx === activeIndex + 1 || idx === activeIndex - 1,
    );
    setDisplayedIndex(newDisplayedIndex);
  }, [activeIndex]);

  if (!items.length) {
    return null;
  }

  const increase = () => {
    activeIndex === items.length - 1 ? setActiveIndex(0) : setActiveIndex(activeIndex + 1);
  };

  const decrease = () => {
    activeIndex === 0 ? setActiveIndex(items.length - 1) : setActiveIndex(activeIndex - 1);
  };

  const { heightClassName } = getHeroDimensions(immersiveSize);
  return (
    <div className={classnames(heightClassName, 'relative overflow-hidden', className)}>
      {items.map((item, idx) => (
        <div
          aria-hidden={idx !== activeIndex}
          className={classnames('absolute inset-0 transition-opacity duration-500 ease-in', {
            'opacity-100': idx === activeIndex,
            'opacity-0': idx !== activeIndex,
          })}
          key={idx}
          tabIndex={-1}
        >
          {displayedIndex[idx] ? item : null}
        </div>
      ))}
      {items.length > 1 && (
        <ImmersiveSlideShowControls
          {...asWidgetProps<ComponentProps<typeof ImmersiveSlideShowControls>>({
            nextLabel: nextLabel,
            activeIndex: activeIndex + 1,
            slidesCount: items.length,
            onNext: increase,
            onPrev: decrease,
            previousLabel: previousLabel,
            isPaginationDisabled: isPaginationDisabled,
            theme: controlsTheme,
          })}
        />
      )}
    </div>
  );
};
